import { Fragment, useEffect, useMemo, useState } from 'react';
import type { AutoConnection } from '../../../core/entities/graph-document';
import type { RelationshipDisplaySummary } from '../../../core/policies/explicit-relationships';
import type { JourneyNode } from '../../../core/entities/node';
import { fallbackAvatarLabel, resolvePresetAvatar } from '../../../core/policies/visual-assets/resolve-preset-avatar';
import { formatNodeTimestamp } from '../../preferences/i18n';
import { useLocale } from '../../preferences/i18n/LocaleProvider';
import {
	loadPresetAvatarCatalog,
	type PresetAvatarCatalog,
} from '../../node-editor/services/preset-avatar-loader';

export function FocusedNodeOverlay({
	node,
	connections,
	originTimeZone,
	onClearFocus,
	onEditNode,
	onManageRelationships,
	onViewRelationshipDetails,
	onDeleteNode,
	relationships,
	editDisabled = false,
	deleteDisabled = false,
	relationshipReadonly = false,
}: {
	node: JourneyNode | null;
	connections: AutoConnection[];
	relationships: FocusedRelationshipSummaryEntry[];
	originTimeZone?: string;
	onClearFocus: () => void;
	onEditNode: () => void;
	onManageRelationships: () => void;
	onViewRelationshipDetails: (pairId: string) => void;
	onDeleteNode: () => void;
	editDisabled?: boolean;
	deleteDisabled?: boolean;
	relationshipReadonly?: boolean;
}) {
	const [catalog, setCatalog] = useState<PresetAvatarCatalog | null>(null);
	const { locale, t } = useLocale();

	useEffect(() => {
		let active = true;

		void loadPresetAvatarCatalog().then((nextCatalog) => {
			if (active) {
				setCatalog(nextCatalog);
			}
		});

		return () => {
			active = false;
		};
	}, []);

	const presetAvatar = useMemo(() => (node ? resolvePresetAvatar(node, catalog) : null), [catalog, node]);

	if (!node) return null;
	const detailGroups = detailGroupsForNode(node, t, locale, originTimeZone);

	return (
		<aside
			aria-label="Focused node"
			className="floating-panel focus-card"
			style={{ position: 'absolute', right: 24, top: 24, width: 420, maxHeight: 'calc(100vh - 48px)', padding: 18, overflowY: 'auto' }}
		>
			<div style={{ display: 'grid', gap: 16 }}>
				<div style={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 16, alignItems: 'center' }}>
					{presetAvatar ? (
						<img
							alt={`${node.displayName} portrait`}
							src={presetAvatar.path}
							style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 24, border: '1px solid rgba(255,255,255,0.18)' }}
						/>
					) : (
						<div
							aria-label={fallbackAvatarLabel(node)}
							style={{
								width: 96,
								height: 96,
								borderRadius: 24,
								display: 'grid',
								placeItems: 'center',
								background:
									'radial-gradient(circle at 35% 30%, rgba(255, 255, 255, 0.32), transparent 30%), linear-gradient(160deg, rgba(99, 230, 255, 0.34), rgba(210, 139, 255, 0.24))',
								border: '1px solid rgba(255,255,255,0.14)',
								boxShadow: '0 18px 48px rgba(1, 8, 24, 0.42)',
								fontSize: 30,
								fontWeight: 700,
								letterSpacing: '0.08em',
							}}
						>
							{initialsForNode(node)}
						</div>
					)}
					<div style={{ display: 'grid', gap: 8 }}>
						<span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(180, 223, 255, 0.84)' }}>
							{nodeTypeLabel(node.type, t)}
						</span>
						<h2 style={{ margin: 0, fontSize: 28, lineHeight: 1.05 }}>{node.displayName}</h2>
						<p style={{ margin: 0, color: 'rgba(231, 240, 255, 0.72)' }}>{node.summary || t('noSummaryYet')}</p>
						<span style={{ fontSize: 12, color: 'rgba(173, 222, 255, 0.72)' }}>{portraitStatus(node, presetAvatar !== null, t)}</span>
					</div>
				</div>

				<section aria-label="Focused node insights" style={{ display: 'grid', gap: 10 }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
						<h3 style={{ margin: 0, fontSize: 14, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(180, 223, 255, 0.84)' }}>
							{t('topConnections')}
						</h3>
						<div className="hud-toolbar" style={{ justifyContent: 'flex-end' }}>
							<button className="hud-subtle-button" disabled={editDisabled} onClick={onEditNode} type="button">
								{t('editAllFields')}
							</button>
							<button className="hud-subtle-button" disabled={deleteDisabled} onClick={onDeleteNode} type="button">
								{t('deleteNode')}
							</button>
							<button className="hud-subtle-button" onClick={onClearFocus} type="button">
								{t('returnToFullMap')}
							</button>
						</div>
					</div>
					<ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 8 }}>
						{connections.slice(0, 3).map((connection) => (
							<li key={connection.connectionId}>
								<strong>{percent(connection.strengthScore)}</strong> {connection.reasonSummary}
							</li>
						))}
					</ol>
				</section>

				<section aria-label="Explicit relationships" style={{ display: 'grid', gap: 10 }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
						<h3 style={{ margin: 0, fontSize: 14, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(180, 223, 255, 0.84)' }}>
							{t('relationshipSummaryTitle')}
						</h3>
						<button className="hud-subtle-button" disabled={relationshipReadonly} onClick={onManageRelationships} type="button">
							{t('manageRelationships')}
						</button>
					</div>
					{relationships.length > 0 ? (
						<ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 8 }}>
							{relationships.map((relationship) => (
								<li key={relationship.pairId} style={{ display: 'grid', gap: 6 }}>
									<strong style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(173, 222, 255, 0.78)' }}>
										{relationship.targetNodeLabel}
									</strong>
									<span>{relationship.summary.summaryText}</span>
									{relationship.summary.edgeKind === 'mixed' ? (
										<span style={{ fontSize: 12, color: 'rgba(255, 226, 181, 0.84)' }}>{t('relationshipMixedSummaryHint')}</span>
									) : null}
									{relationship.summary.hasOverflow ? (
										<span style={{ fontSize: 12, color: 'rgba(180, 223, 255, 0.78)' }}>{t('relationshipSummaryOverflowHint')}</span>
									) : null}
									<button className="hud-subtle-button" onClick={() => onViewRelationshipDetails(relationship.pairId)} type="button">
										{t('viewRelationshipDetails')}
									</button>
								</li>
							))}
						</ul>
					) : (
						<p style={{ margin: 0, color: 'rgba(239, 246, 255, 0.72)' }}>{t('noExplicitRelationships')}</p>
					)}
					{relationshipReadonly ? <p style={{ margin: 0, color: 'rgba(255, 214, 143, 0.92)' }}>{t('relationshipReadonlyNotice')}</p> : null}
				</section>

				<section aria-label="Complete node details" style={{ display: 'grid', gap: 12 }}>
					<h3 style={{ margin: 0, fontSize: 14, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(180, 223, 255, 0.84)' }}>
						{t('completeNodeDetails')}
					</h3>
					<div style={{ display: 'grid', gap: 12 }}>
						{detailGroups.map((group) => (
							<section key={group.title} style={{ display: 'grid', gap: 8 }}>
								<h4 style={{ margin: 0, fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(173, 222, 255, 0.72)' }}>
									{group.title}
								</h4>
								<dl style={{ margin: 0, display: 'grid', gridTemplateColumns: '112px minmax(0, 1fr)', gap: '6px 12px' }}>
									{group.fields.map((field) => (
										<Fragment key={`${group.title}-${field.label}`}>
											<dt style={{ color: 'rgba(173, 222, 255, 0.72)', fontSize: 12 }}>{field.label}</dt>
											<dd style={{ margin: 0, color: 'rgba(239, 246, 255, 0.92)', wordBreak: 'break-word' }}>{field.value}</dd>
										</Fragment>
									))}
								</dl>
							</section>
						))}

						<section style={{ display: 'grid', gap: 6 }}>
							<h4 style={{ margin: 0, fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(173, 222, 255, 0.72)' }}>
								{t('notes')}
							</h4>
							<p style={{ margin: 0, color: 'rgba(239, 246, 255, 0.92)', whiteSpace: 'pre-wrap' }}>{node.notes.trim() || t('noNotes')}</p>
						</section>

						<section style={{ display: 'grid', gap: 6 }}>
							<h4 style={{ margin: 0, fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(173, 222, 255, 0.72)' }}>
								{t('customFields')}
							</h4>
							{node.customFields.length > 0 ? (
								<ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
									{node.customFields.map((field) => (
										<li key={field.fieldId}>
											<strong>{field.label}</strong>: {field.value || t('none')} <span style={{ color: 'rgba(173, 222, 255, 0.72)' }}>({field.valueType})</span>
										</li>
									))}
								</ul>
							) : (
								<p style={{ margin: 0, color: 'rgba(239, 246, 255, 0.92)' }}>{t('noCustomFields')}</p>
							)}
						</section>
					</div>
				</section>
			</div>
		</aside>
	);
}

export interface FocusedRelationshipSummaryEntry {
	pairId: string;
	targetNodeLabel: string;
	summary: RelationshipDisplaySummary;
}

function detailGroupsForNode(
	node: JourneyNode,
	t: ReturnType<typeof useLocale>['t'],
	locale: ReturnType<typeof useLocale>['locale'],
	originTimeZone?: string,
): Array<{ title: string; fields: Array<{ label: string; value: string }> }> {
	const shared = [
		{ label: t('displayName'), value: filled(node.displayName, t) },
		{ label: t('summaryLabel'), value: filled(node.summary, t) },
		{ label: t('tags'), value: list(node.tags, t) },
	];

	const visuals = [
		{ label: t('imageSource'), value: imageSourceLabel(node.imageSource, t) },
		{ label: t('presetAvatarId'), value: filled(node.presetAvatarId, t) },
		{ label: t('uploadedImageAssetId'), value: filled(node.imageAssetId, t) },
	];

	const metadata = [
		{ label: t('nodeId'), value: node.nodeId },
		{ label: t('createdAt'), value: formatNodeTimestamp(node.createdAt, locale, { originTimeZone: node.createdAtTimeZone ?? originTimeZone }) },
		{ label: t('updatedAt'), value: formatNodeTimestamp(node.updatedAt, locale, { originTimeZone: node.updatedAtTimeZone ?? originTimeZone }) },
	];

	if (node.type === 'Human') {
		return [
			{ title: t('coreFields'), fields: shared },
			{ title: t('humanRelations'), fields: [
				{ label: t('occupationIds'), value: list(node.occupationIds, t) },
				{ label: t('projectIds'), value: list(node.projectIds, t) },
				{ label: t('relatedHumanIds'), value: list(node.relatedHumanIds, t) },
			] },
			{ title: t('visualMetadata'), fields: visuals },
			{ title: t('systemMetadata'), fields: metadata },
		];
	}

	if (node.type === 'Project') {
		return [
			{ title: t('coreFields'), fields: shared },
			{ title: t('projectRelations'), fields: [
				{ label: t('participantHumanIds'), value: list(node.participantHumanIds, t) },
				{ label: t('relatedOccupationIds'), value: list(node.relatedOccupationIds, t) },
				{ label: t('relatedProjectIds'), value: list(node.relatedProjectIds, t) },
			] },
			{ title: t('visualMetadata'), fields: visuals },
			{ title: t('systemMetadata'), fields: metadata },
		];
	}

	return [
		{ title: t('coreFields'), fields: shared },
		{ title: t('occupationRelations'), fields: [
			{ label: t('relatedHumanIds'), value: list(node.relatedHumanIds, t) },
			{ label: t('typicalProjectIds'), value: list(node.typicalProjectIds, t) },
		] },
		{ title: t('visualMetadata'), fields: visuals },
		{ title: t('systemMetadata'), fields: metadata },
	];
}

function filled(value: string | null | undefined, t: ReturnType<typeof useLocale>['t']): string {
	return value && value.trim() ? value : t('none');
}

function list(values: string[], t: ReturnType<typeof useLocale>['t']): string {
	return values.length > 0 ? values.join(', ') : t('none');
}

function initialsForNode(node: JourneyNode): string {
	const [first, second] = node.displayName.trim().split(/\s+/);
	return `${first?.[0] ?? 'N'}${second?.[0] ?? ''}`.toUpperCase();
}

function portraitStatus(node: JourneyNode, hasPresetAvatar: boolean, t: ReturnType<typeof useLocale>['t']): string {
	if (node.imageSource === 'upload') return t('uploadedPortraitActive');
	if (node.imageSource === 'preset-avatar' && hasPresetAvatar) return t('presetPortraitActive');
	return t('fallbackPortraitActive');
}

function imageSourceLabel(source: JourneyNode['imageSource'], t: ReturnType<typeof useLocale>['t']): string {
	if (source === 'upload') return t('imageSourceUpload');
	if (source === 'preset-avatar') return t('imageSourcePresetAvatar');
	return t('imageSourceFallback');
}

function nodeTypeLabel(type: JourneyNode['type'], t: ReturnType<typeof useLocale>['t']): string {
	if (type === 'Human') return t('human');
	if (type === 'Project') return t('project');
	return t('occupation');
}

function percent(value: number): string {
	return `${Math.round(value * 100)}%`;
}

